import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react'
import { trackPointer } from '../lib/trackPointer'
import type { CanvasCircle, CanvasRole } from '../services/api'
import {
  circleRadius,
  COLLAPSED_RADIUS,
  distributeLayout,
  DOCUMENT_SIZE,
  hashSeed,
  isExternal,
  isOutput,
  linkCrossesRole,
  portAnchor,
  projectOnCircle,
  pushLayout,
  roleSize,
  seededRandom,
  type CirclePlacement,
  type LayoutItem,
  type PortHit,
  type RolePort,
} from '../lib/circleLayout'

export type { CirclePlacement, PortHit }

interface CircleDocument {
  id: string
  name: string
}

interface CircleBoardProps {
  circle: CanvasCircle
  documents: CircleDocument[]
  showDocuments: boolean
  selectedRoleId: string | null
  open: boolean
  onOpenRole: (role: CanvasRole) => void
  onOpenCircle: () => void
  onDragCircle: (event: React.PointerEvent<HTMLElement>) => void
  onSaveLayout: (positions: Array<{ id: string; x: number; y: number }>) => void
  onRemoveLink: (linkId: string) => void
  onRegister: (element: HTMLElement | null) => void
  onGeometry: (circleId: string, placement: CirclePlacement) => void
  resolvePort: (clientX: number, clientY: number) => PortHit | null
  onConnect: (from: PortHit, to: PortHit | null) => void
  circleAt: (clientX: number, clientY: number) => string | null
  onMoveRole: (roleId: string, circleId: string, clientX: number, clientY: number) => void | Promise<void>
  folded: boolean
  layoutPass: number
  onToggleFold: () => void
}

interface Wire {
  roleId: string
  port: RolePort
  x: number
  y: number
}

/**
 * * One circle: interior role bubbles, a liaison on the rim, and optional document cards.
 * * A dragged role can leave the disk and land in another circle.
 */
export function CircleBoard({
  circle,
  documents,
  showDocuments,
  selectedRoleId,
  open,
  onOpenRole,
  onOpenCircle,
  onDragCircle,
  onSaveLayout,
  onRemoveLink,
  onRegister,
  onGeometry,
  resolvePort,
  onConnect,
  circleAt,
  onMoveRole,
  folded,
  layoutPass,
  onToggleFold,
}: CircleBoardProps) {
  const boardRef = useRef<HTMLElement>(null)
  const saveRef = useRef(onSaveLayout)
  saveRef.current = onSaveLayout
  const geometryRef = useRef(onGeometry)
  geometryRef.current = onGeometry
  const registerRef = useRef(onRegister)
  registerRef.current = onRegister
  const resolveRef = useRef(resolvePort)
  resolveRef.current = resolvePort
  const connectRef = useRef(onConnect)
  connectRef.current = onConnect
  const circleAtRef = useRef(circleAt)
  circleAtRef.current = circleAt
  const moveRef = useRef(onMoveRole)
  moveRef.current = onMoveRole
  const documentSpots = useRef<Record<string, { x: number; y: number }>>({})
  const [placed, setPlaced] = useState<CirclePlacement>(() => place(circle, documents, showDocuments, 'keep', seededRandom(hashSeed(circle.id)), {}))
  const placedRef = useRef(placed)
  placedRef.current = placed
  const [wire, setWire] = useState<Wire | null>(null)
  const [lifting, setLifting] = useState(false)
  const docKey = showDocuments ? documents.map((document) => document.id).join() : ''
  const structureKey = `${circle.id}|${circle.roles.map((role) => role.id).join()}|${docKey}`
  const appliedKey = useRef('')
  const appliedDocs = useRef<string | null>(null)
  const appliedPass = useRef(layoutPass)

  useEffect(() => {
    registerRef.current(boardRef.current)
    return () => registerRef.current(null)
  }, [circle.id])

  useEffect(() => {
    geometryRef.current(circle.id, placedRef.current)
  }, [circle.id, placed])

  useEffect(() => {
    const passChanged = appliedPass.current !== layoutPass
    const structureChanged = appliedKey.current !== structureKey
    if (!passChanged && !structureChanged) {
      return
    }
    const previousDocs = appliedDocs.current
    appliedKey.current = structureKey
    appliedDocs.current = docKey
    appliedPass.current = layoutPass
    const missing = circle.roles.some((role) => role.layoutX == null || role.layoutY == null)
    const documentsChanged = previousDocs !== null && previousDocs !== docKey
    const mode = passChanged || missing || documentsChanged ? 'fresh' : 'keep'
    const random = passChanged ? Math.random : seededRandom(hashSeed(structureKey))
    const full = place(circle, documents, showDocuments, mode, random, documentSpots.current)
    rememberDocuments(full.items, documentSpots.current)
    const visual = folded ? collapsePlacement(full) : full
    placedRef.current = visual
    setPlaced(visual)
    if (mode === 'fresh' || positionsDiffer(circle, full.items)) {
      saveRef.current(rolePositions(full.items))
    }
  }, [circle, documents, showDocuments, structureKey, docKey, folded, layoutPass])

  const diameter = placed.radius * 2
  const center = placed.radius

  const toLocal = (point: { clientX: number; clientY: number }) => {
    const rect = boardRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) {
      return { x: 0, y: 0 }
    }
    const scale = rect.width / diameter
    return {
      x: (point.clientX - rect.left) / scale - center,
      y: (point.clientY - rect.top) / scale - center,
    }
  }

  const redistribute = () => {
    const next = place(circle, documents, showDocuments, 'fresh', Math.random, {})
    rememberDocuments(next.items, documentSpots.current)
    placedRef.current = next
    setPlaced(next)
    onSaveLayout(rolePositions(next.items))
  }

  const dragBubble = (event: React.PointerEvent<HTMLElement>, item: LayoutItem) => {
    event.stopPropagation()
    event.preventDefault()
    const origin = { x: item.x, y: item.y }
    const start = toLocal(event)
    let moved = false
    setLifting(true)
    const move = (ev: PointerEvent) => {
      const local = toLocal(ev)
      if (!moved && Math.hypot(local.x - start.x, local.y - start.y) < 4) {
        return
      }
      moved = true
      setPlaced((current) => {
        const dragged = current.items.map((node) => {
          if (node.id !== item.id) {
            return node
          }
          if (node.ring) {
            return { ...node, ...projectOnCircle(origin.x + local.x - start.x, origin.y + local.y - start.y, current.radius) }
          }
          return { ...node, x: origin.x + local.x - start.x, y: origin.y + local.y - start.y }
        })
        const next = { radius: current.radius, items: pushLayout(dragged, item.id, current.radius, item.kind === 'role' && !item.ring) }
        placedRef.current = next
        return next
      })
    }
    const up = (ev: PointerEvent) => {
      setLifting(false)
      if (!moved) {
        const role = circle.roles.find((candidate) => candidate.id === item.id)
        if (role) {
          onOpenRole(role)
        }
        return
      }
      rememberDocuments(placedRef.current.items, documentSpots.current)
      if (item.kind === 'role' && !item.ring) {
        const destination = circleAtRef.current(ev.clientX, ev.clientY)
        if (destination && destination !== circle.id) {
          const restore = () => {
            const snapped = {
              radius: placedRef.current.radius,
              items: pushLayout(placedRef.current.items, null, placedRef.current.radius, false),
            }
            placedRef.current = snapped
            setPlaced(snapped)
          }
          void Promise.resolve(moveRef.current(item.id, destination, ev.clientX, ev.clientY)).catch(restore)
          return
        }
      }
      const next = {
        radius: placedRef.current.radius,
        items: pushLayout(placedRef.current.items, null, placedRef.current.radius, false),
      }
      placedRef.current = next
      setPlaced(next)
      rememberDocuments(next.items, documentSpots.current)
      onSaveLayout(rolePositions(next.items))
    }
    trackPointer(event, move, up)
  }

  const dragPort = (event: React.PointerEvent<HTMLButtonElement>, roleId: string, port: RolePort) => {
    event.stopPropagation()
    event.preventDefault()
    const move = (ev: PointerEvent) => {
      const local = toLocal(ev)
      setWire({ roleId, port, x: local.x, y: local.y })
    }
    const up = (ev: PointerEvent) => {
      const hit = resolveRef.current(ev.clientX, ev.clientY)
      setWire(null)
      connectRef.current({ circleId: circle.id, roleId, port }, hit)
    }
    trackPointer(event, move, up)
  }

  return (
    <section
      ref={boardRef}
      className="absolute"
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) {
          return
        }
        onDragCircle(event)
      }}
      style={{
        left: circle.x,
        top: circle.y,
        width: diameter,
        height: diameter,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 50% 42%, #f6f1e7 0%, #e7e2d8 70%)',
        border: '1px solid #d6d3d1',
        cursor: 'grab',
        boxShadow: open ? '0 0 0 3px var(--color-robot-blue)' : '0 8px 24px rgba(55, 65, 81, 0.08)',
        zIndex: lifting ? 6 : open ? 2 : 1,
        overflow: 'visible',
      }}
    >
      <header
        className="cursor-grab"
        onPointerDown={onDragCircle}
        style={{ position: 'absolute', top: folded ? 18 : 14, left: 16, right: 16, zIndex: 5, textAlign: 'center' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, maxWidth: '100%' }}>
          <button
            type="button"
            className="text-sm font-semibold text-primary"
            onClick={onOpenCircle}
            onPointerDown={(event) => event.stopPropagation()}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {circle.implicit ? 'Cercle' : circle.purpose}
          </button>
          <button
            type="button"
            title={folded ? 'Déplier le cercle' : 'Replier le cercle'}
            aria-label={folded ? `Déplier ${circle.purpose || 'le cercle'}` : `Replier ${circle.purpose || 'le cercle'}`}
            onClick={onToggleFold}
            onPointerDown={(event) => event.stopPropagation()}
            style={foldButtonStyle()}
          >
            {folded ? <ChevronsUpDown className="w-3 h-3" /> : <ChevronsDownUp className="w-3 h-3" />}
          </button>
        </span>
        {folded ? null : (
          <div>
            <button
              type="button"
              className="text-xs text-secondary"
              title="Répartir les rôles"
              onClick={redistribute}
              onPointerDown={(event) => event.stopPropagation()}
            >
              Répartir
            </button>
          </div>
        )}
      </header>
      <svg
        width={diameter}
        height={diameter}
        style={{ position: 'absolute', inset: 0, zIndex: 3, overflow: 'visible', pointerEvents: 'none' }}
      >
        {folded ? null : (circle.links ?? []).filter((link) => link.kind !== 'external').map((link) => {
          const source = placed.items.find((item) => item.id === link.sourceRoleId)
          const target = placed.items.find((item) => item.id === link.targetRoleId)
          if (!source || !target) {
            return null
          }
          const from = portAnchor(source, 'out')
          const to = portAnchor(target, 'in')
          const dotted = linkCrossesRole(from.x, from.y, to.x, to.y, placed.items, source.id, target.id)
          return (
            <LinkShape
              key={link.id}
              x1={center + from.x}
              y1={center + from.y}
              x2={center + to.x}
              y2={center + to.y}
              dotted={dotted}
              external={false}
              onRemove={() => onRemoveLink(link.id)}
            />
          )
        })}
        {wire ? <WireLine wire={wire} items={placed.items} center={center} /> : null}
      </svg>
      {placed.items.map((item) => {
        if (item.kind === 'document') {
          const name = documents.find((document) => `doc:${document.id}` === item.id)?.name ?? 'Document'
          return (
            <div
              key={item.id}
              title={name}
              onPointerDown={(event) => dragBubble(event, item)}
              style={{
                position: 'absolute',
                left: center + item.x - item.width / 2,
                top: center + item.y - item.height / 2,
                width: item.width,
                height: item.height,
                zIndex: lifting ? 6 : 2,
                cursor: 'grab',
                background: 'var(--color-warning-light)',
                border: '1px solid #d6d3d1',
                borderRadius: 2,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: '0 12px 12px 0',
                  borderColor: 'transparent var(--color-warning) transparent transparent',
                }}
              />
              <span className="text-xs text-primary" style={{ display: 'block', padding: '12px 6px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </span>
            </div>
          )
        }
        const role = circle.roles.find((candidate) => candidate.id === item.id)
        if (!role) {
          return null
        }
        const selected = role.id === selectedRoleId
        const ports: RolePort[] = role.isLiaison
          ? ['in', 'out', 'external-in', 'external-out']
          : role.communicationInterne
            ? ['in', 'out']
            : []
        return (
          <div
            key={item.id}
            onPointerDown={item.collapsed ? onDragCircle : undefined}
            style={{
              position: 'absolute',
              left: center + item.x - item.width / 2,
              top: center + item.y - item.height / 2,
              width: item.width,
              height: item.height,
              zIndex: lifting ? 6 : 2,
              cursor: item.collapsed ? 'grab' : undefined,
            }}
          >
            {ports.map((port) => (
              <PortButton
                key={port}
                item={item}
                port={port}
                label={portLabel(role, port)}
                onPointerDown={(event) => dragPort(event, role.id, port)}
              />
            ))}
            {item.collapsed ? null : (
            <button
              type="button"
              title={role.purpose || role.accountabilities?.[0] || role.domains?.[0] || role.name}
              onPointerDown={(event) => dragBubble(event, item)}
              className="text-xs text-primary"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: 999,
                border: role.isLiaison ? '1px solid var(--color-warning)' : '1px solid #d6d3d1',
                background: selected ? 'var(--color-robot-blue-light)' : '#f3f4f6',
                boxShadow: '0 1px 2px rgba(55, 65, 81, 0.18)',
                padding: '0 10px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {role.name}
            </button>
            )}
          </div>
        )
      })}
    </section>
  )
}

export function LinkShape({
  x1,
  y1,
  x2,
  y2,
  dotted,
  external,
  onRemove,
}: {
  x1: number
  y1: number
  x2: number
  y2: number
  dotted: boolean
  external: boolean
  onRemove: () => void
}) {
  const mx = (x1 + x2) / 2
  const my = (y1 + y2) / 2
  const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI)
  const color = dotted ? '#9ca3af' : external ? 'var(--color-warning)' : 'var(--color-robot-blue-dark)'
  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={dotted ? 1.25 : 2}
        strokeDasharray={dotted ? '2 5' : undefined}
        strokeLinecap="round"
      />
      <g transform={`translate(${mx} ${my}) rotate(${angle})`} style={{ pointerEvents: 'auto', cursor: 'pointer' }}>
        <polygon points="7,0 -6,5 -6,-5" fill={color} />
        <circle r={11} fill="transparent" onClick={onRemove}>
          <title>Retirer le lien</title>
        </circle>
      </g>
    </g>
  )
}

function place(
  circle: CanvasCircle,
  documents: CircleDocument[],
  showDocuments: boolean,
  mode: 'keep' | 'fresh',
  random: () => number,
  spots: Record<string, { x: number; y: number }>,
): CirclePlacement {
  const interior = circle.roles.filter((role) => !role.isLiaison).length + (showDocuments ? documents.length : 0)
  const radius = circleRadius(Math.max(1, interior))
  const roles: LayoutItem[] = circle.roles.map((role) => {
    const ring = role.isLiaison
    const fallback = ring ? projectOnCircle(role.layoutX ?? 0, role.layoutY ?? 0, radius) : { x: role.layoutX ?? 0, y: role.layoutY ?? 0 }
    return {
      id: role.id,
      kind: 'role' as const,
      ring,
      x: role.layoutX ?? fallback.x,
      y: role.layoutY ?? fallback.y,
      ...roleSize(role.name),
    }
  })
  const cards: LayoutItem[] = showDocuments
    ? documents.map((document) => {
      const id = `doc:${document.id}`
      const spot = spots[id]
      return {
        id,
        kind: 'document' as const,
        x: spot?.x ?? 0,
        y: spot?.y ?? 0,
        ...DOCUMENT_SIZE,
      }
    })
    : []
  const items = [...roles, ...cards]
  const links = (circle.links ?? [])
    .filter((link) => link.kind !== 'external')
    .map((link) => ({ sourceId: link.sourceRoleId, targetId: link.targetRoleId }))
  return {
    radius,
    items: mode === 'fresh' ? distributeLayout(items, links, radius, random) : pushLayout(items, null, radius),
  }
}

function collapsePlacement(full: CirclePlacement): CirclePlacement {
  const liaison = full.items.find((item) => item.ring && item.kind === 'role')
  if (!liaison) {
    return { radius: COLLAPSED_RADIUS, items: [] }
  }
  const span = COLLAPSED_RADIUS * 2 - 28
  return {
    radius: COLLAPSED_RADIUS,
    items: [{
      ...liaison,
      ring: false,
      collapsed: true,
      x: 0,
      y: 0,
      width: span,
      height: span,
    }],
  }
}

function foldButtonStyle(): CSSProperties {
  return {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 4,
    border: '1px solid #d6d3d1',
    background: '#f3f4f6',
    color: '#4b5563',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}

function rememberDocuments(items: LayoutItem[], spots: Record<string, { x: number; y: number }>): void {
  for (const item of items) {
    if (item.kind === 'document') {
      spots[item.id] = { x: item.x, y: item.y }
    }
  }
}

function rolePositions(items: LayoutItem[]): Array<{ id: string; x: number; y: number }> {
  return items.filter((item) => item.kind === 'role').map((item) => ({ id: item.id, x: item.x, y: item.y }))
}

function positionsDiffer(circle: CanvasCircle, items: LayoutItem[]): boolean {
  return circle.roles.some((role) => {
    const item = items.find((candidate) => candidate.id === role.id)
    if (!item) {
      return false
    }
    if (role.layoutX == null || role.layoutY == null) {
      return true
    }
    return Math.abs(item.x - role.layoutX) > 0.5 || Math.abs(item.y - role.layoutY) > 0.5
  })
}

function WireLine({ wire, items, center }: { wire: Wire; items: LayoutItem[]; center: number }) {
  const source = items.find((item) => item.id === wire.roleId)
  const from = source ? portAnchor(source, wire.port) : { x: wire.x, y: wire.y }
  return (
    <line
      x1={center + from.x}
      y1={center + from.y}
      x2={center + wire.x}
      y2={center + wire.y}
      stroke={isExternal(wire.port) ? 'var(--color-warning)' : 'var(--color-robot-blue)'}
      strokeWidth={1.5}
      strokeDasharray="3 4"
    />
  )
}

function PortButton({
  item,
  port,
  label,
  onPointerDown,
}: {
  item: LayoutItem
  port: RolePort
  label: string
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void
}) {
  const anchor = portAnchor(item, port)
  const external = isExternal(port)
  const output = isOutput(port)
  const style: CSSProperties = {
    position: 'absolute',
    left: anchor.x - item.x + item.width / 2 - 6,
    top: anchor.y - item.y + item.height / 2 - 6,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: external ? '1px solid var(--color-warning)' : '1px solid #d6d3d1',
    background: output ? (external ? 'var(--color-warning)' : 'var(--color-robot-blue)') : '#e5e7eb',
    zIndex: 4,
    padding: 0,
  }
  return (
    <button type="button" title={label} aria-label={label} onPointerDown={onPointerDown} style={style} />
  )
}

function portLabel(role: CanvasRole, port: RolePort): string {
  if (!role.isLiaison) {
    return port === 'out' ? `Sortie de ${role.name}` : `Entrée de ${role.name}`
  }
  if (port === 'external-out') {
    return `Sortie hors du cercle de ${role.name}`
  }
  if (port === 'external-in') {
    return `Entrée hors du cercle de ${role.name}`
  }
  if (port === 'out') {
    return `Sortie dans le cercle de ${role.name}`
  }
  return `Entrée dans le cercle de ${role.name}`
}
